import React, { useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import styled from "styled-components";

const Container = styled.div`
  color: rgb(119, 119, 119);
  @media (max-width: 768px) {
    ul {
      padding-left: 1.2rem;
    }
  }
`;

const LastUpdate = styled.p`
  font-size: 14px;
`;

const Link = styled.a`
  color: #014651 !important;
  text-decoration: underline !important;
`;

const FlexBox = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 20px;
  flex-wrap: wrap;
`;

const ContactUsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const ContactUs = styled.div`
  display: flex;
`;

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <Container className="container my-5">
      <h2 className="text-center mb-2">PRIVACY POLICY</h2>
      <LastUpdate className="text-center">Last updated [Jan.2025]</LastUpdate>
      <p className="text-left mb-4">
        RIZE for Operation and Management of Digital Payment Smart Solutions
        (e-Novate) (S.A.E)
      </p>

      <div className="mt-4">
        <h4>1. INTRODUCTION</h4>
        <p>
          This Privacy Policy for <strong>RIZE</strong>, a trademark owned by{" "}
          <strong>e-Novate (S.A.E)</strong>, explains how and why{" "}
          <strong>RIZE</strong> collects, uses, stores, shares, or otherwise
          processes information when the user interacts with{" "}
          <strong>RIZE's</strong> services. This document is designed to help
          you understand what information we handle, how we protect it, and your
          rights regarding your data.
        </p>
        <ol>
          <li>
            <strong>e-Novate</strong> will receive all necessary User data
            directly from the <strong>University</strong>, not from the User
            themselves. The App operates exclusively with the data provided by
            the University, ensuring that User information is managed securely
            and in accordance with applicable laws. No personal data is
            collected directly through the App.
          </li>
          <li>
            By accessing or using our Services, you agree to the terms outlined
            in this Privacy Policy. If you do not agree with any aspect of these
            terms, we advise you not to use the App or any related services.
          </li>
          <li>
            The Services to which this Privacy Policy applies include, but are
            not limited to:
            <ol type="i">
              <li>
                <strong>Use of the RIZE App:</strong> The App will operate
                solely based on data provided by the University to deliver its
                intended functionality and services.
              </li>
              <li>
                <strong>Visit to RIZE's Website:</strong> Users may access our{" "}
                <Link href="https://www.rizecard.app/">website</Link> or any
                other website that links to this Privacy Policy.
              </li>
              <li>
                <strong>Use of Cookies and Tracking Technologies:</strong> Our
                website uses cookies and similar technologies to enhance your
                browsing experience, analyze website performance, and deliver
                personalized content. These cookies may collect information
                about browsing behavior, preferences, and device details. By
                using our website, you consent to the use of cookies as
                described in this Privacy Policy. You may adjust your cookie
                preferences via your browser settings or by visiting our Cookie
                Policy.
              </li>
              <li>
                <strong>Engagement with RIZE through Other Channels:</strong>{" "}
                This includes interactions via sales, marketing, events, or
                related activities.
              </li>
            </ol>
          </li>
          <li>
            We encourage you to read this Privacy Policy thoroughly to
            understand your privacy rights and the measures we take to safeguard
            your data. If you have any questions, concerns, or need further
            clarification, please do not hesitate to contact{" "}
            <strong>RIZE</strong> at help@rizecard.app.
          </li>
          <li>
            <strong>
              Your privacy matters to us, and we are committed to protecting
              your personal information.
            </strong>
          </li>
        </ol>
      </div>

      <div className="mt-4 ps-4">
        <h4>A. DATA PROCESSING PRINCIPLES IN RIZE’S PRIVACY POLICY</h4>
        <h6>Internet Protocol (IP) Address</h6>
        <p className="mb-1">Precise Legal Definition:</p>
        <p>
          IP address is a unique address that devices use in order to identify
          and communicate with each other on a computer network.
        </p>
      </div>
      <div className="mt-4 ps-4">
        <h6>Cookie</h6>
        <p className="mb-1">Precise Legal Definition:</p>
        <p>
          Cookie is a very small text file sent by a web server and stored on
          your hard drive, your computer’s memory, or in your browser so that it
          can be read back later. Cookies are a basic way for a server to
          identify the computer you happen to be using at the time.
        </p>
      </div>
      <div className="mt-4 ps-4">
        <h6>Service Provider </h6>
        <p className="mb-1">Precise Legal Definition:</p>
        <p>
          In order to provide a full range of online services, we may use
          various third-party providers. These third parties provide services
          such as: website hosting, bill payment, and account aggregation.
          Third-party providers are referred to within this policy as "Service
          Providers."
        </p>
      </div>
      <div className="mt-4 ps-4">
        <h6>Personal Data </h6>
        <p className="mb-1">
          <p className="mb-1">Precise Legal Definition:</p>
          “is any information related to an identified or identifiable natural
          person (‘data subject’); an identifiable natural person is one who can
          be identified, directly or indirectly, in particular by reference to
          an identifier such as a name, an identification number, location data,
          an online identifier or to one or more factors specific to the
          physical, physiological, genetic, mental, economic, cultural or social
          identity of that natural person;”
        </p>
        <p className="mb-1 mt-3">Simplified Definition:</p>
        <p className="mb-1">
          Information about a person that can directly or indirectly identify
          them, like:
        </p>
        <FlexBox>
          <ul className="list-disc pl-6">
            <li>Names</li>
            <li>E-mail address</li>
            <li>ID numbers</li>
            <li>Addresses</li>
          </ul>
          <ul>
            <li>Precise geolocation</li>
            <li>IP addresses</li>
            <li>Biometric data</li>
            <li>Bank account information not including “C.V.V” number</li>
          </ul>
        </FlexBox>
      </div>
      <div className="mt-4 ps-4">
        <h6>Processing </h6>
        <p className="mb-1">
          <p className="mb-1">Precise Legal Definition:</p>
          “is any operation or set of operations which is performed on personal
          data or on sets of personal data, whether or not by automated means,
          such as collection, recording, organisation, structuring, storage,
          adaptation or alteration, retrieval, consultation, use, disclosure by
          transmission, dissemination or otherwise making available, alignment
          or combination, restriction, erasure or destruction;”
        </p>
        <p className="mb-1 mt-3">Simplified Definition:</p>
        <p className="mb-1">
          Doing any of the following actions to a piece or set of personal data:
        </p>
        <FlexBox>
          <ul className="list-disc pl-5">
            <li>Collecting</li>
            <li>Recording</li>
            <li>Organizing</li>
            <li>Structuring</li>
            <li>Storing</li>
          </ul>
          <ul className="list-disc pl-5">
            <li>Adapting</li>
            <li>Altering</li>
            <li>Retrieving</li>
            <li>Consulting</li>
            <li>Using</li>
          </ul>
          <ul className="list-disc pl-5">
            <li>Disclosing</li>
            <li>Disseminating</li>
            <li>Making available</li>
            <li>Aligning</li>
          </ul>
          <ul className="list-disc pl-5">
            <li>Combining</li>
            <li>Restricting</li>
            <li>Erasing</li>
            <li>Destroying</li>
          </ul>
        </FlexBox>
      </div>
      <div className="mt-4 ps-4">
        <h6>Consent </h6>
        <p className="mb-1">
          <p className="mb-1">Precise Legal Definition:</p>
          "is any freely given, specific, informed and unambiguous indication of
          the data subject’s wishes by which he or she, by a statement or by a
          clear affirmative action, signifies agreement to the processing of
          personal data relating to him or her."
        </p>
        <p className="mb-1 mt-3">Simplified Definition:</p>
        <p className="mb-1">
          When a data subject freely agrees to data processing by taking an
          affirmative, clear action (like selecting a checkbox, clicking a
          button labelled ‘I Agree’, writing it on a piece of paper, or signing
          a document with this purpose) and has access to and read a compliant
          privacy policy that informs them about the entity’s data processing
          activities. *This is an important definition to pay attention to if
          the User's business relies on consent as a legal basis for processing
          personal data.
        </p>
      </div>
      <div className="mt-4 ps-4">
        <h6>Data Controller </h6>
        <p className="mb-1">
          <p className="mb-1">Precise Legal Definition:</p>
          "The natural or legal person, public authority, agency or other body
          which, alone or jointly with others, determines the purposes and means
          of the processing of personal data; where the purposes and means of
          such processing are determined by Union or Member State law, the
          controller or the specific criteria for its nomination may be provided
          for by Union or Member State law;"
        </p>
        <p className="mb-1 mt-3">Simplified Definition:</p>
        <p className="mb-1">
          Any natural person or entity that determines the reason for and means
          of processing personal data pertaining to data subjects (e.g.,
          customers, users, website visitors, etc.).
        </p>
        <p className="mb-1">University Means: which provide all data to rize</p>
      </div>
      <div className="mt-4 ps-4">
        <h6>University</h6>
        <p className="mb-1">
          <p className="mb-1">Precise Legal Definition:</p>
          "University" refers the educational institution or legal entity that,
          alone or jointly with others, determines the purposes and means of
          processing personal data provided to RIZE for the execution of its
          services. The University acts as the Data Controller, supplying all
          necessary data to RIZE while ensuring compliance with applicable data
          protection laws.
        </p>
        <p className="mb-1 mt-3">Simplified Definition:</p>
        <p className="mb-1">
          "University" means: institution that provides all required data to
          RIZE for service delivery and is responsible for deciding how and why
          the data is processed.
        </p>
      </div>
      <div className="mt-4 ps-4">
        <h6>Data Processor </h6>
        <p className="mb-1">
          <p className="mb-1">Precise Legal Definition:</p>a natural or legal
          person, public authority, agency or other body which processes
          personal data on behalf of the controller.
        </p>
        <p className="mb-1 mt-3">Simplified Definition:</p>
        <p className="mb-1">
          Any third party tasked by a data controller to process personal data
          based on the instructions provided by the controller and on their
          behalf.
        </p>
      </div>
      <div className="mt-4 ps-4">
        <h6>Tracking Technologies</h6>
        <p className="mb-1">We may use Tracking Technologies, such as:</p>
        <ul>
          <li>
            Web Beacons: Small graphic files or pixels embedded in emails or web
            pages to track user engagement.
          </li>
          <li>
            Web Beacons: Small graphic files or pixels embedded in emails or web
            pages to track user engagement.
          </li>
          <li>
            Web Beacons: Small graphic files or pixels embedded in emails or web
            pages to track user engagement.
          </li>
        </ul>
        <p className="mb-1">How We Use Tracking Technologies:</p>
        <ul>
          <li>To ensure the proper functionality of our website.</li>
          <li>To enhance user experience and improve website usability.</li>
          <li>To analyze visitor behavior and gather statistical insights.</li>
          <li>To serve relevant advertisements and marketing content .</li>
          <li>To maintain security and prevent fraudulent activity.</li>
        </ul>
      </div>
      <div className="mt-4 ps-4">
        <h4>B. GENERAL PRINCIPLES OF RIZE’S PRIVACY POLICY</h4>
        <p>
          RIZE’S Privacy Policy is based on the general principles that guide
          the vision of data protection, following the guidelines of Data
          Protection and Privacy Laws respected worldwide like the GDPR and CCPA
          among others.
        </p>
        <p>
          The principles are Lawfulness, Transparency, Fairness, and
          Accountability.
        </p>
        <p>
          Data is collected and processed lawfully, transparently, fairly, and
          with accountability for the purpose of:
        </p>
        <ol type="i">
          <li>
            Purpose Limitation: Collect data for specified, explicit, and
            legitimate purposes only.
          </li>
          <li>
            Data Minimization: Ensure data is adequate, relevant, and limited to
            what is necessary.
          </li>
          <li>Accuracy: Keep data accurate and up-to-date.</li>
          <li>
            Storage Limitation: Retain data only as long as necessary for its
            purpose.
          </li>
          <li>
            Integrity and Confidentiality: Process data securely to protect
            against unauthorized or unlawful processing, accidental loss,
            destruction, or damage.
          </li>
        </ol>

        <h4>Lawfulness</h4>
        <p>
          RIZE’S data processing activities are conducted in accordance with
          applicable data protection laws and regulations, including but not
          limited to the GDPR, CCPA, and other relevant regional laws. RIZE
          ensures that its processing activities are based on one or more lawful
          grounds, such as:
        </p>
        <ol type="i">
          <li>
            Consent: Data subjects have provided explicit consent for the
            processing of their personal data for specific purposes.
          </li>
          <li>
            Contractual Necessity: Processing is necessary for the performance
            of a contract to which the data subject is a party or for taking
            pre-contractual steps at the data subject's request.
          </li>
          <li>
            Legal Obligation: Processing is necessary for compliance with a
            legal obligation to which RIZE is subject.
          </li>
          <li>
            Legitimate Interests: Processing is necessary for the purposes of
            RIZE’S legitimate interests or those of a third party, provided that
            such interests are not overridden by the interests or fundamental
            rights and freedoms of the data subject.
          </li>
        </ol>

        <h4>Transparency</h4>
        <p>
          RIZE is committed to transparency in data processing practices. To
          ensure transparency, RIZE provides detailed information about:
        </p>
        <ol type="i">
          <li>The types of personal data RIZE collects and processes.</li>
          <li>
            The purposes for which RIZE collects and processes personal data.
          </li>
          <li>The legal basis for processing personal data.</li>
          <li>
            The categories of recipients with whom RIZE shares personal data.
          </li>
          <li>
            The rights of data subjects and how they can exercise those rights.
          </li>
          <li>
            Any automated decision-making processes, including profiling, and
            their implications for data subjects.
          </li>
          <li>
            Updates or changes to RIZE’S privacy policy and the reasons for such
            updates or changes.
          </li>
        </ol>
      </div>

      <div className="mt-4 ps-4">
        <h4>Fairness</h4>
        <p>
          RIZE is committed to treating all individuals equally and ensuring
          that data processing practices do not result in unfair or
          discriminatory treatment. RIZE does not discriminate against
          individuals on the basis of race, ethnicity, religion, gender, sexual
          orientation, disability, or any other protected characteristic.
        </p>

        <h4>Accountability</h4>
        <p>
          RIZE is accountable for data processing practices and is committed to
          demonstrating compliance with applicable data protection laws and
          regulations. To fulfil accountability obligations, RIZE:
        </p>
        <ol type="i">
          <li>
            Maintains records of data processing activities, including the
            purposes of processing, categories of personal data processed, and
            security measures implemented.
          </li>
          <li>
            Conducts data protection impact assessments (DPIAs) for high-risk
            processing activities.
          </li>
          <li>
            Cooperates with data protection authorities and responds to
            inquiries and requests for information in a timely and transparent
            manner.
          </li>
        </ol>

        <h4>Privacy by Design and Default</h4>
        <p>
          RIZE implements privacy by design and default principles and practices
          in data processing activities to ensure that privacy considerations
          are incorporated into systems, processes, and practices from the
          outset. This includes:
        </p>
        <ol type="i">
          <li>
            Minimizing the collection and processing of personal data to what is
            necessary for the intended purpose.
          </li>
          <li>
            Implementing appropriate technical and organizational measures to
            ensure the security and confidentiality of personal data.
          </li>
          <li>
            Regularly assessing and mitigating privacy risks associated with
            RIZE’s data processing activities.
          </li>
        </ol>

        <h4>C. SUMMARY OF KEY POINTS</h4>
        <p>
          This summary provides key points from RIZE’S Privacy Policy, but the
          User can find out more details about any of these topics by using the
          table of contents below to find the section they are looking for.
        </p>

        <ul>
          <li>
            <strong>What Personal Information does RIZE process?</strong>
            <br></br>
            When the User visits, uses, and/or navigates the Services, RIZE may
            process Personal Information depending on how the User interacts
            with the Services, the choices the User makes, and the products and
            features used.
          </li>
          <li>
            <strong>
              Does RIZE process any sensitive Personal Information?
            </strong>{" "}
            <br></br>
            RIZE does not process sensitive Personal Information. RIZE may
            process sensitive Personal Information when necessary, with the
            User’s consent or as otherwise permitted by applicable law.
          </li>
          <li>
            <strong>
              Does RIZE receive, or share any information from/with third
              parties?
            </strong>{" "}
            <br></br>
            RIZE may receive/share information from public databases, marketing
            partners, social media platforms, and other outside sources.
          </li>
          <li>
            <strong>How does RIZE process User information?</strong>
            <br></br> RIZE processes the User's information to provide, improve,
            and administer the Services, communicate with the User, for security
            and fraud prevention, and to comply with law. RIZE may also process
            the User's information for other purposes with the User's consent.
            RIZE processes the User's information only when there is a valid
            legal reason to do so.
          </li>
          <li>
            <strong>
              In what situations and with which types of parties does RIZE share
              Personal Information?
            </strong>{" "}
            <br></br>
            RIZE may share information in specific situations and with specific
            categories of third parties.
          </li>
          <li>
            <strong>How does RIZE keep the User's information safe?</strong>{" "}
            <br></br>
            RIZE has organizational and technical processes and procedures in
            place to protect the User's Personal Information. However, no
            electronic transmission over the internet or information storage
            technology can be guaranteed to be 100% secure, so RIZE cannot
            promise or guarantee that hackers, cybercriminals, or other
            unauthorized third parties shall not be able to defeat the security
            and improperly collect, access, steal, or modify the User's
            information.
          </li>
          <li>
            <strong>What are the User's rights?</strong>
            <br></br>
            Depending on where the User is located geographically, the
            applicable privacy law may mean that the User has certain rights
            regarding Personal Information.
          </li>
          <li>
            <strong>How does the User exercise his/her rights?</strong>
            <br></br>
            The easiest way to exercise the User's rights is by filling out a
            data subject access request by contacting RIZE. RIZE shall consider
            and act upon any request in accordance with applicable data
            protection laws.
          </li>
        </ul>
      </div>
      <div className="mt-5">
        <h4>2. LEGAL BASIS</h4>
        <p>
          RIZE only processes Personal Information when it is necessary and RIZE
          has a valid legal basis to do so under applicable law, such as the
          User's consent, to comply with laws, to provide the User with services
          to enter into or fulfill RIZE’S contractual obligations, to protect
          the User's rights, or to fulfill RIZE’S legitimate business interests.
        </p>

        <h4 className="mt-5">3. WHAT DATA DOES RIZE COLLECT</h4>
        <div className="ps-4">
          <h4>A. Personal Information the User Discloses to RIZE</h4>
          <p>
            RIZE gathers Personal Information that the university willingly
            provides when engaging in activities on the Services or contacting
            RIZE through various channels such as websites, telephone, postal
            mail, social media, forums, or other methods.
          </p>

          <h4>B. Personal Information Provided by the User</h4>
          <p>
            The types of Personal Information collected by RIZE vary depending
            on the user's interactions with the Services, their choices, and the
            specific products and features they utilize, including:
          </p>
          <ol type="i">
            <li>
              <strong>Account Information:</strong> When the User creates an
              account with RIZE, RIZE shall collect information associated with
              the User's account, including their name, contact information,
              account credentials, payment card information, and transaction
              history.
            </li>
            <li>
              <strong>User Content:</strong> When the User uses the Services,
              RIZE shall collect Personal Information that is included in the
              input, file uploads, or feedback provided to RIZE’S Services.
            </li>
            <li>
              <strong>Communication Information:</strong> If the User
              communicates with RIZE, RIZE shall collect their name, contact
              information, and the contents of any messages sent.
            </li>
            <li>
              <strong>Social Media Information:</strong> When the User interacts
              with RIZE’s social media pages, RIZE may collect Personal
              Information provided by the User and receive aggregate information
              and analytics from the hosting platforms.
            </li>
            <li>
              <strong>Other Information:</strong> Information provided by the
              User during events, surveys, or when establishing their identity.
            </li>
            <li>
              <strong>Automatically Collected Information:</strong> Data
              gathered when the User interacts with the Services, including:
              <ol type="1">
                <li>
                  <strong>Log Data:</strong> Includes IP address, browser type
                  and settings, timestamps, and user interactions.
                </li>
                <li>
                  <strong>Usage Data:</strong> Covers content interactions,
                  feature usage, user actions, time zone, and access dates.
                </li>
                <li>
                  <strong>Device Information:</strong> Includes device name,
                  operating system, identifiers, and browser used.
                </li>
                <li>
                  <strong>Cookies:</strong> Used to operate and improve the
                  User's experience on the Services.
                </li>
                <li>
                  <strong>Location Data:</strong> Information about the User's
                  device location, collected through GPS or similar
                  technologies.
                </li>
                <li>
                  <strong>Analytics:</strong> Data collected via cookies for
                  analyzing usage patterns and improving the User experience.
                </li>
              </ol>
            </li>
          </ol>

          <h4>C. SENSITIVE INFORMATION</h4>
          <p>
            RIZE DOES NOT PROCESS SENSITIVE INFORMATION UNLESS NECESSARY, WITH
            THE USER'S CONSENT OR AS OTHERWISE PERMITTED BY APPLICABLE LAW.
          </p>

          <h4>D. Payment Data</h4>
          <p>
            RIZE may collect data necessary to process the User's payment
            information if the User makes purchases using a RIZE card, such as
            the User's payment or transaction number (e.g., card number). All
            payment data is stored by [eNovate].
          </p>
        </div>
      </div>
      <div className="mt-5">
        <h4>4. HOW DOES RIZE USE DATA</h4>
        <p>RIZE uses data that it collects to:</p>
        <div className="ps-4">
          <h4>A. Service Provision and Maintenance</h4>
          <ol type="i">
            <li>
              Provide Services to the User, including account registration, and
              maintain the Platform and User's account, facilitating account
              creation and authentication.
            </li>
            <li>Market and sell products and Services.</li>
            <li>Process payments securely.</li>
            <li>Send administrative information to the User.</li>
            <li>
              Ensure safe, secure, and reliable Services and Sites operation.
            </li>
            <li>
              Enhance operational efficiency and deliver improved services to
              the User.
            </li>
            <li>Respond to User inquiries.</li>
            <li>
              Send messages and information regarding RIZE’S products and
              services.
            </li>
            <li>
              Distribute support and administrative messages, technical notices,
              updates, alerts, and other relevant information.
            </li>
            <li>
              Inform the User about services and programs offered by RIZE that
              may be of interest.
            </li>
            <li>
              Identify and prevent fraud and abuse, including identity
              verification.
            </li>
            <li>Fulfil and manage the User's orders.</li>
            <li>Handle accounting, audits, and internal functions.</li>
            <li>
              Comply with requests or orders from courts, law enforcement, or
              government authorities.
            </li>
            <li>
              Share information with third parties in cases of detected abuse of
              the Site or Services.
            </li>
            <li>
              Provide customer support and assistance and communication with the
              User.
            </li>
            <li>Enable user-to-user communications.</li>
            <li>Request feedback.</li>
            <li>Save or protect an individual's vital interest.</li>
          </ol>

          <h4>B. User Engagement and Experience</h4>
          <ol type="i">
            <li>
              Gather feedback and conduct surveys for service improvement.
            </li>
            <li>
              Customize User experience based on preferences and behavior.
            </li>
            <li>Organize events and promotions for user engagement.</li>
            <li>Offer rewards or loyalty programs.</li>
            <li>Facilitate user-generated content creation and sharing.</li>
            <li>Monitor and enforce community guidelines.</li>
            <li>Provide tutorials or educational resources.</li>
            <li>
              Implement social features for networking and interaction among
              users.
            </li>
            <li>Offer premium features or exclusive content access.</li>
          </ol>

          <h4>C. Partnerships and Marketing</h4>
          <ol type="i">
            <li>Collaborate with partners for joint marketing efforts.</li>
            <li>Collaborate with influencers for promotional campaigns.</li>
            <li>
              Develop partnerships with other companies for cross-promotion
              opportunities.
            </li>
          </ol>

          <h4>D. Data Analysis and Research</h4>
          <ol type="i">
            <li>Analyze User data for insights and trends.</li>
            <li>Conduct market research and analysis.</li>
            <li>Provide recommendations for related products or services.</li>
          </ol>

          <h4>E. Content Creation and Promotion</h4>
          <ol type="i">
            <li>
              Recommend artists to business partners for publishing and
              promotional purposes.
            </li>
            <li>Create and curate playlists or content collections.</li>
            <li>Host contests or competitions.</li>
            <li>
              Integrate with third-party platforms for enhanced functionality.
            </li>
            <li>Deliver targeted advertising to the User.</li>
            <li>Identify usage trends.</li>
            <li>Determine the effectiveness of RIZE’S marketing campaigns.</li>
          </ol>
        </div>
        <h4 className="mt-5">5. HOW RIZE MANAGES DATA</h4>
        <div className="ps-4">
          <h4>A. Protection</h4>
          <p>
            Data protection applies to all users of any services owned or
            controlled by RIZE. This section also includes Service Providers,
            Contractors, Affiliates, Vendors, Agents, Entities, their parent
            company and subsidiaries, as well as their employees, officers,
            directors, members, managers, shareholders, vendors, licensors,
            licensees, customers, successors, and assigns.
          </p>
          <p>
            Each party must comply with all legislation and regulatory
            requirements concerning the use of personal data, including U.S.
            Data Protection laws such as the California Consumer Privacy Act
            (CCPA) and the California Privacy Rights Act (CPRA), as well as the
            General Data Protection Regulation (GDPR). The User is not permitted
            to rent or sell customer lists, contact details, or other data
            without explicit prior consent from the customers.
          </p>

          <h4>B. Data Retention and Destruction of Information</h4>
          <p>
            RIZE retains the User's information in compliance with relevant laws
            concerning retention periods and only for as long as necessary to
            fulfill the purposes for which it was collected. Retention may also
            be necessary for essential business purposes, such as complying with
            legal obligations, resolving disputes, and enforcing agreements.
          </p>
          <p>
            Upon the User's request, information will be destroyed unless legal
            requirements specify otherwise. The destruction will ensure
            confidentiality, considering the sensitivity and importance of the
            data.
          </p>
          <p>
            If the User deletes their account, data removal will occur within 15
            days from the termination date. If ongoing business needs prevent
            immediate deletion, RIZE will securely store the User's Personal
            Information and isolate it from any further processing until
            deletion is possible.
          </p>
          <h4>C. Data Storage</h4>
          <p>
            Personal data is stored on secure servers located within the regions
            RIZE operates. RIZE ensures that the data is protected under
            applicable data protection laws and regulations. RIZE implements
            appropriate administrative, technical, and physical safeguards to
            ensure the confidentiality, integrity, and availability of the
            User's personal data.
          </p>

          <h4>D. Data Security</h4>
          <p>
            RIZE is committed to ensuring the security of personal data and
            employs various security measures to maintain the safety of personal
            data, including:
          </p>
          <ol type="i">
            <li>
              <strong>Encryption:</strong> RIZE uses encryption technologies to
              protect data during transmission and at rest.
            </li>
            <li>
              <strong>Access Controls:</strong> Access to personal data is
              restricted to authorized personnel based on job necessity.
            </li>
            <li>
              <strong>Firewalls and Intrusion Detection:</strong> Systems are
              protected by firewalls and intrusion detection systems to prevent
              unauthorized access.
            </li>
            <li>
              <strong>Regular Security Audits:</strong> Regular audits and
              assessments are conducted to identify and mitigate potential
              vulnerabilities.
            </li>
          </ol>

          <h4>E. Data Security Measures</h4>
          <p>
            To ensure data protection, RIZE implements the following measures:
          </p>
          <ol type="i">
            <li>
              Regular training for employees on data protection best practices
              and policies.
            </li>
            <li>
              Incident response plans to promptly address data breaches or
              security incidents.
            </li>
            <li>
              Periodic third-party security assessments to ensure compliance
              with industry standards.
            </li>
            <li>
              Data minimization, collecting and retaining only the minimum
              personal data necessary.
            </li>
            <li>
              Secure disposal of personal data through methods like shredding,
              degaussing, or electronic deletion.
            </li>
          </ol>
          <p>
            Despite these safeguards, no electronic transmission or storage
            technology can guarantee 100% security. However, RIZE commits to
            following best practices to protect personal data. Users should
            access the Services within a secure environment.
          </p>

          <h4>F. Data Breach Notifications</h4>
          <p>
            In the event of a data breach involving personal data, RIZE will
            notify affected individuals and relevant authorities without undue
            delay, as required by applicable data protection laws and
            regulations.
          </p>

          <h4>G. Children-Minor’s Data</h4>
          <p>
            RIZE is provided with data by the University and does not solicit
            data from or market to children under the age of 18. Data is
            processed solely for providing services such as analysis,
            decision-making, monitoring progress, solving problems, and
            contributing to the body of knowledge. If required, RIZE will
            deactivate the account and delete such data promptly.
          </p>
        </div>

        <h4 className="mt-5">6. USER’S RIGHTS</h4>
        <div className="ps-4">
          <h4>A. Rights Under the General Data Protection Regulation (GDPR)</h4>
          <p>The User has the following rights regarding personal data:</p>
          <ol type="i">
            <li>
              <strong>Right to Access:</strong> Obtain confirmation of whether
              personal data is being processed and access the data and
              information about its processing.
            </li>
            <li>
              <strong>Right to Rectification:</strong> Request correction of
              inaccurate personal data and completion of incomplete personal
              data.
            </li>
            <li>
              <strong>Right to Erasure (Right to be Forgotten):</strong> Request
              erasure of personal data under certain circumstances.
            </li>
            <li>
              <strong>Right to Restriction of Processing:</strong> Request
              restriction of processing under certain circumstances.
            </li>
            <li>
              <strong>Right to Data Portability:</strong> Receive personal data
              in a structured, commonly used, machine-readable format and
              transmit it to another controller without hindrance.
            </li>
            <li>
              <strong>Right to Object:</strong> Object to the processing of
              personal data under certain circumstances.
            </li>
            <li>
              <strong>Right to Withdraw Consent:</strong> Withdraw consent at
              any time where consent is the legal basis for processing the
              User’s data.
            </li>
            <li>
              <strong>Right to Know:</strong>
              <ul>
                <li>
                  Whether RIZE collects and uses the User's Personal
                  Information.
                </li>
                <li>The categories of Personal Information collected.</li>
                <li>
                  The purposes for which the collected Personal Information is
                  used.
                </li>
                <li>The specific pieces of Personal Information collected.</li>
                <li>
                  In accordance with applicable law, RIZE is not obligated to
                  provide or delete consumer information that is de-identified
                  in response to a consumer request or to re-identify individual
                  data to verify a request.
                </li>
              </ul>
            </li>
          </ol>
          <div className="mt-4">
            <h4>
              B. Limiting Use and Disclosure of Sensitive Personal Information
            </h4>
            <p>
              If RIZE collects sensitive Personal Information, the User has the
              right to limit its use to only what’s necessary for services. Once
              requested, RIZE won’t use or disclose this information for other
              purposes without consent, excluding publicly available or
              non-characteristic inference data.
            </p>

            <h4>C. Verification Process</h4>
            <p>
              Upon receiving a request, RIZE will verify the User's identity to
              ensure the information matches their records. This may involve
              asking for previously provided information or using other
              verification methods. Information provided for verification will
              only be used for that purpose and will be deleted afterward.
            </p>

            <h4>D. Right to Non-Discrimination</h4>
            <p>
              The User has the right not to receive discriminatory treatment for
              exercising privacy rights.
            </p>

            <h4>E. Other Privacy Rights</h4>
            <p>
              The User can object to Personal Information processing, request
              data correction or restriction, and designate authorized agents.
              RIZE may deny requests from unauthorized agents. Opt-out requests
              for selling or sharing Personal Information will be processed
              within fifteen days.
            </p>

            <h4>F. Account Management</h4>
            <p>
              Users can review or update their account information by logging in
              or contacting RIZE. Upon account termination request, RIZE will
              deactivate or delete the account, retaining some information for
              legal or operational purposes.
            </p>

            <h4>G. Do Not Track</h4>
            <p>
              RIZE respects the User’s privacy preferences and acknowledges the
              "Do Not Track" signals sent by web browsers. RIZE does not track
              User’s online activities over time and across third-party websites
              or services, except as required for service provision or permitted
              by applicable law.
            </p>

            <h4>H. Opt-Out Mechanisms</h4>
            <p>
              RIZE does not sell the User’s Personal Information. Users can
              opt-out of limited data sharing for targeted digital advertising.
              RIZE does not maintain web tracking data in an identifiable
              manner.
            </p>
          </div>
        </div>

        <h4>Exercising User Rights</h4>
        <p>
          To exercise any of these rights or inquire about personal data
          processing, please contact RIZE using the information in the “Contact
          Us” section.
        </p>

        <h4 className="mt-5">7. USER REQUESTS, COMPLAINTS AND NOTICES</h4>
        <p>
          RIZE shall respond to User requests, complaints, and notices within
          120 days of receiving the request. RIZE may extend the response time
          by an additional 30 days if necessary, with notification provided
          within the initial 30 days.
        </p>

        <h4 className="mt-5">
          8. USER RESIDENCY IN RELATION TO PRIVACY AND DATA PROTECTION LAWS
        </h4>
        <div className="ps-4">
          <h4>A. Data Processing and Storage</h4>
          <p>
            Personal Information shall be processed and stored in Egypt. RIZE
            may also disclose User information to service providers and
            affiliates in other jurisdictions.
          </p>

          <h4>B. General Principles</h4>
          <p>
            RIZE’s data protection practices are guided by principles of
            lawfulness, fairness, transparency, purpose limitation, data
            minimization, accuracy, storage limitation, integrity,
            confidentiality, and accountability.
          </p>

          <h4>C. General Compliance Statement</h4>
          <p>
            RIZE adheres to applicable data protection laws in the regions where
            users are located, demonstrating commitment to compliance.
          </p>

          <h4>D. Governance</h4>
          <p>
            RIZE complies with relevant data protection laws and regulations in
            regions where it operates, including Egypt’s Personal Data
            Protection Laws.
          </p>

          <h4>E. Specific Provisions for Key Regions</h4>
          <p>
            RIZE complies with specific data protection laws and guidelines
            applicable in Egypt.
          </p>
        </div>
        <h4 className="pt-5">9. DISCLOSURE OF INFORMATION</h4>
        <div className="ps-4">
          <h4>A. General Disclosure Policy</h4>
          <p>
            RIZE does not sell, trade, or transfer personal data to outside
            parties without User consent, except as described in this policy.
          </p>
          <ol type="i">
            <li>
              <strong>Legal Obligations:</strong> Disclosure to government
              authorities, law enforcement, or other entities as required by
              law.
            </li>
            <li>
              <strong>Business Transfers:</strong> Data may be transferred
              during mergers, acquisitions, or asset sales.
            </li>
          </ol>

          <h4>B. Specific Circumstances for Disclosure</h4>
          <ol type="i">
            <li>
              <strong>Vendors and Service Providers:</strong> Data shared for
              business operations, subject to RIZE’s instructions.
            </li>
            <li>
              <strong>Business Transfers:</strong> Data shared during strategic
              transactions.
            </li>
            <li>
              <strong>Legal Requirements:</strong> Shared for legal compliance,
              fraud prevention, or protection of rights.
            </li>
            <li>
              <strong>Affiliates:</strong> Data shared with affiliates as
              outlined in this policy.
            </li>
          </ol>

          <h4>C. Additional Disclosure Circumstances</h4>
          <ol type="i">
            <li>
              <strong>Consent:</strong> Disclosures made with User consent.
            </li>
            <li>
              <strong>Emergency Circumstances:</strong> To protect User
              interests if unable to provide consent.
            </li>
            <li>
              <strong>Employment Necessity:</strong> Data shared for employment
              or social security benefits administration.
            </li>
            <li>
              <strong>Public Information:</strong> Information made public by
              the User.
            </li>
            <li>
              <strong>Archiving:</strong> Data shared for archival and research
              purposes.
            </li>
            <li>
              <strong>Contract Performance:</strong> Data shared for contract
              administration.
            </li>
            <li>
              <strong>Legal Obligation:</strong> Data shared in compliance with
              applicable laws.
            </li>
            <li>
              <strong>Service Providers:</strong> Data shared under contractual
              safeguards.
            </li>
            <li>
              <strong>Affiliates:</strong> Data shared for relevant purposes
              aligned with this policy.
            </li>
            <li>
              <strong>De-Identified and Aggregate Information:</strong> Used and
              disclosed without limitations.
            </li>
          </ol>
        </div>
        <h4 className="pt-5">10. DISPUTE RESOLUTION</h4>
        <p>
          Disputes arising from this policy shall first be resolved through
          informal negotiation. If unresolved, formal arbitration will be
          conducted by the Cairo Regional Centre for International Commercial
          Arbitration (CRCICA). The arbitration will be settled by three
          arbitrators, with proceedings conducted in Cairo, Egypt, and the
          language of arbitration being Arabic. The final decision will be
          binding, and both parties waive any rights to participate in class
          action lawsuits or class-wide arbitration.
        </p>

        <h4 className="pt-5">11. UPDATES TO PRIVACY POLICY</h4>
        <p>
          RIZE regularly reviews and updates this privacy policy to ensure
          compliance with data protection laws and best practices. Changes will
          be communicated to Users in a timely manner, and continued use of
          RIZE’s services implies acceptance of these updates.
        </p>
        <ContactUsContainer>
          <h4 className="pt-5 mb-3">CONTACT US</h4>
          <p>
            For any questions or concerns about privacy practices or this
            policy, please contact RIZE at:
          </p>
          <ContactUs>
            <p className="me-5">
              Email: <a href="mailto:help@rizecard.app">help@rizecard.app</a>
            </p>
            <p>Phone: 0235317310</p>
          </ContactUs>{" "}
          <p>
            RIZE is dedicated to addressing any issues or inquiries promptly and
            transparently.
          </p>
        </ContactUsContainer>
      </div>
    </Container>
  );
};

export default PrivacyPolicy;
